import { ReactNode, Component, ReactElement } from 'react';
import _ from 'lodash';
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { ButtonThemeType } from '@UI/Widgets/Things/UserInput/FormStruct';
import { SettingsList } from '@Primitive/SettingsList';
import { SettingsDialogConf, SettingsType } from '@Primitive/SettingsListType';
import { Canvas, CanvasItemDescDictionary, CanvasItemLayoutDescDictionary } from '@UI/Canvas/Canvas';
import { IsNullOrWhiteSpace, IsNullOrEmpty, isDefined, getRandomKey, getRandomSmallKey } from "@Utils/Micro"
import { Button, ButtonLabelPosition, ButtonValueType } from "@Primitive/Button";
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { getRowMinHeight } from '@Primitive/Typography';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { red200 } from '@Utils/Colors';
import { yHas } from '@Utils/Utils';
// =======================================================================================================
export interface ButtonsDataSettings {
    Key: string;
    Value: string;
    Label: string;
    LabelPosition: ButtonLabelPosition;
    LabelAlign: "center" | "left" | "right";
    Tooltip: string;
    Type: ButtonValueType;
    ThemeType: ButtonThemeType;
    Flat: boolean;
    Uppercase: boolean;
    Color: string;
    FontColor: string;
    ShowBubble?: boolean;
    BubbleContent?: string;
    BubbleBackgroundColor?: string;
    BubbleColor?: string;
    BubbleSize?: number;
}
// ------------------------------------------------------------------------------------------------------
export interface ButtonsData {
    Buttons: ButtonsDataSettings[];
    IsSingleton?: boolean;
    FillWidget: boolean;
    CanvasLayoutDesc: CanvasItemLayoutDescDictionary;
    MaxFontSize: number;
    BorderRadius: number;
    OnHoverFontColor: string;
    OnHoverBackgroundColor: string;
}
// ------------------------------------------------------------------------------------------------------
export interface ButtonsState {
    hoveredButtonKey: string;
}
// ------------------------------------------------------------------------------------------------------
export class Buttons extends Component<WidgetsThingsStructs.PartialIWidgetThingsProps<ButtonsData>, ButtonsState> {
    // ------------------------------------------------------------------------------------------------------
    private _canvasLayout: CanvasItemLayoutDescDictionary;
    private _canvasKey: string;
    // ------------------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<ButtonsData>) {
        super(props);

        // Check if widget's data need migration.
        const widgetData: ButtonsData = _.cloneDeep(this.props.WidgetData);
        this._canvasKey = getRandomKey();
        this._canvasLayout = _.cloneDeep(widgetData.CanvasLayoutDesc)
        Canvas.CompactLayoutDesc(this._canvasLayout);

        this.state = {
            hoveredButtonKey: null
        };
    }
    // ------------------------------------------------------------------------------------------------------
    isSingleton(props: WidgetsThingsStructs.PartialIWidgetThingsProps<ButtonsData>): boolean {
        return _.isEqual(props.WidgetData.IsSingleton, true);
    }
    // ---------------------------------------------------------------------------------------------------
    private _getTotalRows(layoutDesc: CanvasItemLayoutDescDictionary): number {
        let rows: number = 0;

        // find the last row
        _.forEach(layoutDesc, layout => {
            if (rows < layout.Y + layout.H)
                rows = layout.Y + layout.H;
        });

        return rows;
    }
    // ---------------------------------------------------------------------------------------------------
    private _getReactNodeForButton(btn: ButtonsDataSettings, width: number, height: number): ReactElement {
        const portKey = btn.Key;

        let color = undefined;
        let bgColor = 'white';

        switch (btn.ThemeType) {
            case ButtonThemeType.Primary:
                bgColor = 'rgb(0, 188, 212)';
                if (btn.Flat) {
                    color = 'rgb(0, 188, 212)';
                } else {
                    color = 'white';
                }
                break;
            case ButtonThemeType.Secondary:
                bgColor = 'rgb(255, 64, 129)';
                if (btn.Flat) {
                    color = 'rgb(255, 64, 129)';
                } else {
                    color = 'white';
                }
                break;
            case ButtonThemeType.Custom:
                if (!_.isEmpty(btn.Color)) {
                    bgColor = btn.Color;
                }
                if (!_.isEmpty(btn.FontColor)) {
                    color = btn.FontColor;
                }
                break;
            default:
                break;
        }

        // apply hover effect(s)
        let className;
        if (portKey == this.state.hoveredButtonKey) {
            let hoverColor = this.props.WidgetData.OnHoverFontColor;
            if (!IsNullOrEmpty(hoverColor)) {
                color = hoverColor;
            }

            let hoverBgColor = this.props.WidgetData.OnHoverBackgroundColor;
            if (!IsNullOrEmpty(hoverBgColor)) {
                bgColor = hoverBgColor;
                className = 'no-hover';
            }
        }

        let label = this.props.LocalizationMap[portKey];
        if (!isDefined(label) || IsNullOrEmpty(label))
            label = btn.Label;

        let tooltip = this.props.LocalizationMap[getKeyForTooltip(portKey)];
        if (!isDefined(tooltip) || IsNullOrEmpty(tooltip))
            tooltip = btn.Tooltip;

        let lineHeight = "unset";
        if ((btn.Type == ButtonValueType.Text) ||
            (btn.LabelPosition == ButtonLabelPosition.left ||
                btn.LabelPosition == ButtonLabelPosition.left_str ||
                btn.LabelPosition == ButtonLabelPosition.right ||
                btn.LabelPosition == ButtonLabelPosition.right_str))
            lineHeight = height + "px";

        return (
            <div
                // (un)capture mouse over this button
                onMouseEnter={() => this.setState({ hoveredButtonKey: portKey })}
                onMouseLeave={() => this.setState({ hoveredButtonKey: null })}>
                <Button
                    key={'button-' + portKey}
                    className={className}
                    width={width}
                    height={height}
                    borderRadius={this.props.WidgetData.BorderRadius}
                    shadowLevel={(btn.Flat) ? 0 : 1}

                    // Label
                    label={label}
                    labelPosition={btn.LabelPosition}
                    labelAutoSize={true}
                    labelMaxFontSize={this.props.WidgetData.MaxFontSize}
                    style={{
                        width: width,
                        height: height,
                        lineHeight: lineHeight,
                        overflow: 'hidden',
                        backgroundColor: (btn.Flat) ? undefined : bgColor,
                        color: color,
                        textTransform: btn.Uppercase ? 'uppercase' : 'initial',
                        justifyContent: "center"
                    }}
                    styleLabel={{
                        textAlign: btn.LabelAlign ?? "center"
                    }}
                    tooltip={tooltip}

                    showBubble={btn.ShowBubble}
                    bubbleColor={btn.BubbleColor}
                    bubbleBackgroundColor={btn.BubbleBackgroundColor}
                    bubbleContent={btn.BubbleContent}
                    bubbleSize={btn.BubbleSize}

                    icon={btn.Value}
                    iconType={btn.Type}
                    iconFontColor={color}
                    iconFontSize={24}

                    onClick={() => this.props.SendPortEvent(portKey, true)} />
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<ButtonsData>) {
        if (!_.isEqual(this.props.WidgetData.CanvasLayoutDesc, this._canvasLayout)) {
            this._canvasLayout = _.cloneDeep(this.props.WidgetData.CanvasLayoutDesc);

            if (this.props.WidgetData.FillWidget)
                Canvas.CompactLayoutDesc(this._canvasLayout);
        }
        else if (this.props.WidgetData.FillWidget &&
            this.props.WidgetData.FillWidget != prevProps.WidgetData.FillWidget)
            Canvas.CompactLayoutDesc(this._canvasLayout);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        if (this.isSingleton(this.props))
            return this._getReactNodeForButton(
                this.props.WidgetData.Buttons[0],
                this.props.width,
                this.props.height
            );
        else {
            let canvasItemDesc: CanvasItemDescDictionary = {};

            // Set `Render` for each button.
            this.props.WidgetData.Buttons.forEach(btn => {
                canvasItemDesc[btn.Key] = {
                    Key: btn.Key,
                    zDepth: 0,
                    Render: (key: string, width: number, height: number): ReactNode =>
                        this._getReactNodeForButton(btn, width, height)
                };
            });

            let minHeight = (!this.props.WidgetData.FillWidget) ? 36 :
                getRowMinHeight(this._getTotalRows(this._canvasLayout), this.props.height, 10, 5);

            return (
                <Canvas
                    key={this._canvasKey}
                    width={this.props.width}
                    height={this.props.height}
                    WidthListThreshold={0}
                    SizeW={6}
                    Layout={{
                        LayoutDesc: this._canvasLayout,
                        ItemDesc: canvasItemDesc
                    }}
                    GridLayout={true}
                    CompactLayout={true}
                    InEditMode={false}
                    Margin={{ W: 2, H: 2 }}
                    extraCanvasMargin={2}
                    Padding={2}
                    CanvasBackgroundColor='transparent'
                    ElementSettings={{
                        zDepth: 0,
                        NoBackground: true,
                        BackgroundColor: "transparent",
                        BorderRadius: 0,
                        Overflow: "initial"
                    }}
                    HeightPixels={minHeight}
                    rootStyle={{
                        width: 'initial',
                        height: 'initial',
                        overflow: 'initial'
                    }} />
            );
        }
    }
}

// =======================================================================================================
export interface ButtonsWidgetSettingsState {
    WidgetData: ButtonsData;
    canvasItems: CanvasItemDescDictionary;
}

interface ILayoutEditor {
    layout: CanvasItemLayoutDescDictionary;
    canvasItems: CanvasItemDescDictionary;
}

export class ButtonsWidgetSettings extends Component<IWidgetSettingsProps<ButtonsData>, ButtonsWidgetSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    static colorPickerDivs: HTMLElement[] = [];
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<ButtonsData>) {
        super(props);
        const widgetData: ButtonsData = _.cloneDeep(this.props.WidgetData);
        this.state = {
            WidgetData: widgetData,
            canvasItems: ButtonsWidgetSettings.setCanvasItems(widgetData)
        };
    }
    // ---------------------------------------------------------------------------------------------------
    isSingleton(props: IWidgetSettingsProps<ButtonsData>): boolean {
        return _.isEqual(props.WidgetData.IsSingleton, true);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.WidgetData);
    }
    // ---------------------------------------------------------------------------------------------------
    static setCanvasItems(wd: ButtonsData): CanvasItemDescDictionary {
        let obj: CanvasItemDescDictionary = {};
        _.forOwn(wd.CanvasLayoutDesc, l => {
            // Define item's title.
            let title = '';
            const element = _.find(wd.Buttons, b => b.Key == l.Key);
            if (isDefined(element)) {
                switch (element.Type) {
                    case ButtonValueType.Text:
                        title = element.Label;
                        break;
                    case ButtonValueType.FontIcon:
                        title = '<i class="fa ' + element.Value + '"></i>';
                        break;
                    case ButtonValueType.Image:
                        title = '<img src="' + element.Value + '" style="height: 33px; width: auto;" />';
                        break;
                    default:
                        title = element.Label;
                        break;
                }

                // Set existing item's descriptor.
                obj[l.Key] = {
                    Key: l.Key,
                    Title: title,
                    Render: () => { return null; }
                };
            }
        });
        return obj;
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const props = this.props;

        if (this.isSingleton(this.props))
            return <SettingsList
                AutoSave={true}
                Data={this.state.WidgetData}
                SettingsMap={[
                    {
                        name: '',
                        elements: [
                            {
                                type: SettingsType.DropDown,
                                label: "Type",
                                tooltip: "Defines the label's format (available types: Text, Icon, Image).",
                                dataPath: "Buttons[0].Type",
                                items: [
                                    { value: ButtonValueType.Text, label: "Text" },
                                    { value: ButtonValueType.FontIcon, label: "Icon" },
                                    { value: ButtonValueType.Image, label: "Image" }
                                ]
                            },
                            {
                                type: SettingsType.String,
                                label: "Label",
                                tooltip: 'Based on its type the label will have the corresponding format.',
                                dataPath: "Buttons[0].Label"
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Label Position",
                                tooltip: "Defines the label's position.",
                                dataPath: "Buttons[0].LabelPosition",
                                items: [
                                    { value: ButtonLabelPosition.bottom, label: "Bottom" },
                                    { value: ButtonLabelPosition.top, label: "Top" },
                                    { value: ButtonLabelPosition.left, label: "Left" },
                                    { value: ButtonLabelPosition.right, label: "Right" }
                                ],
                                isHidden: (d) => d.Buttons[0].Type == ButtonValueType.Text
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Label Alignment",
                                tooltip: "Defines the label's alignment.",
                                dataPath: "Buttons[0].LabelAlign",
                                items: [
                                    { value: "center", label: "Center" },
                                    { value: "left", label: "Left" },
                                    { value: "right", label: "Right" },
                                ],
                                isHidden: (d) => d.Buttons[0].Type == ButtonValueType.Text
                            },
                            {
                                type: SettingsType.Generic,
                                typeFunc: (d) => (d.Buttons[0].Type == ButtonValueType.FontIcon) ? SettingsType.Icon : SettingsType.String,
                                label: "Image / Icon",
                                dataPath: "Buttons[0].Value",
                                isHidden: (d) => d.Buttons[0].Type == ButtonValueType.Text
                            },
                            {
                                type: SettingsType.String,
                                label: "Tooltip",
                                dataPath: "Buttons[0].Tooltip"
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Theme",
                                tooltip: 'Defines the colors of button according to theme (available themes: Default, Primary, Secondary, Custom).',
                                dataPath: "Buttons[0].ThemeType",
                                items: [
                                    { value: ButtonThemeType.Default, label: "Default" },
                                    { value: ButtonThemeType.Primary, label: "Primary" },
                                    { value: ButtonThemeType.Secondary, label: "Secondary" },
                                    { value: ButtonThemeType.Custom, label: "Custom" },
                                ]
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: "Color",
                                tooltip: "Used only when button's type is Custom and specifies its color.",
                                dataPath: "Buttons[0].Color",
                                isHidden: (d) => d.Buttons[0].ThemeType != ButtonThemeType.Custom
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: "Font Color",
                                tooltip: 'Font Color',
                                dataPath: "Buttons[0].FontColor",
                                isHidden: (d) => d.Buttons[0].ThemeType != ButtonThemeType.Custom
                            },
                            {
                                type: SettingsType.Boolean,
                                label: "Flat",
                                tooltip: 'Reduces the amount of layering on the screen, making it more readable.',
                                dataPath: "Buttons[0].Flat"
                            },
                            {
                                type: SettingsType.Boolean,
                                label: "Uppercase",
                                tooltip: 'Transforms all characters to uppercase.',
                                dataPath: "Buttons[0].Uppercase"
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Max Font Size',
                                tooltip: 'Sets the maximum font size.',
                                dataPath: 'MaxFontSize',
                                minValue: 12,
                                maxValue: 40,
                                stepValue: 1,
                                valuePosfix: 'px'
                            }
                        ]
                    },
                    {
                        name: 'Bubble indicator',
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Has Bubble',
                                dataPath: 'Buttons[0].ShowBubble',
                                tooltip: 'If enabled, show the bubble indicator'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'Bubble Text Color',
                                tooltip: 'Color for the bubble text',
                                dataPath: 'Buttons[0].BubbleColor',
                                isHidden: (d) => !d.Buttons[0].ShowBubble
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'Bubble Bg Color',
                                tooltip: 'Color of bubble background',
                                dataPath: 'Buttons[0].BubbleBackgroundColor',
                                isHidden: (d) => !d.Buttons[0].ShowBubble
                            },
                            {
                                type: SettingsType.String,
                                label: "Bubble Content",
                                tooltip: 'Set Bubble Content.',
                                dataPath: "Buttons[0].BubbleContent",
                                isHidden: (d) => !d.Buttons[0].ShowBubble
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Bubble size',
                                tooltip: 'Configure bubble size.',
                                dataPath: 'Buttons[0].BubbleSize',
                                minValue: 20,
                                maxValue: 40,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: (d) => !d.Buttons[0].ShowBubble
                            }
                        ]
                    }
                ]}
                onSaveData={(data) => this.setState({ WidgetData: data }, () => this.saveData())}
                Toolbar={this.props.SettingListToolbar}
            />
        else
            //todo: fix copy at playground; do we really need `canvasItem`
            return <SettingsList
                AutoSave={true}
                Data={this.state}
                SettingsMap={[
                    {
                        name: '',
                        elements: [
                            {
                                type: SettingsType.ItemsMgmtDialog,
                                label: 'Buttons',
                                tooltip: 'Sets the properties of buttons(Label,Label Position,Image/Icon,Type,Tooltip,Theme,Color,Font Color,Flat,Uppercase)',
                                dataPath: 'WidgetData.Buttons',
                                isHidden: (data) => data.WidgetData.IsSingleton,
                                dialogStaticSize: { w: 0.95, h: 0.7 },
                                itemsInfo: [
                                    {
                                        type: SettingsType.String,
                                        label: "Label",
                                        tooltip: "Defines the button's text label.",
                                        dataPath: "Label"
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: "Label Position",
                                        tooltip: "Defines the label's position from the icon (available types: Bottom, Top, Left, Right).",
                                        dataPath: "LabelPosition",
                                        items: [
                                            { value: ButtonLabelPosition.bottom, label: "Bottom" },
                                            { value: ButtonLabelPosition.top, label: "Top" },
                                            { value: ButtonLabelPosition.left, label: "Left" },
                                            { value: ButtonLabelPosition.right, label: "Right" }
                                        ],
                                        isHiddenCell: (d, r, i) => r.Type == ButtonValueType.Text
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: "Label Align",
                                        tooltip: "Defines the label's align.",
                                        dataPath: "LabelAlign",
                                        items: [
                                            { value: "center", label: "Center" },
                                            { value: "left", label: "Left" },
                                            { value: "right", label: "Right" }
                                        ],
                                        isHiddenCell: (d, r, i) => r.Type == ButtonValueType.Text
                                    },
                                    {
                                        type: SettingsType.Generic,
                                        typeFunc: (d) => SettingsType.Generic,
                                        typeFuncCell: (d, r, i) => (r.Type == ButtonValueType.FontIcon) ? SettingsType.Icon : SettingsType.String,
                                        tooltip: "Used only when button's type is Icon or Image and defines its icon or image url.",
                                        label: "Image / Icon",
                                        dataPath: "Value",
                                        isHiddenCell: (d, r, i) => r.Type == ButtonValueType.Text
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: "Type",
                                        tooltip: "Defines the label's format (available types: Text, Icon, Image).",
                                        dataPath: "Type",
                                        items: [
                                            { value: ButtonValueType.Text, label: "Text" },
                                            { value: ButtonValueType.FontIcon, label: "Icon" },
                                            { value: ButtonValueType.Image, label: "Image" }
                                        ]
                                    },
                                    {
                                        type: SettingsType.String,
                                        label: "Tooltip",
                                        tooltip: "Button's tooltip",
                                        dataPath: "Tooltip"
                                    },
                                    {
                                        type: SettingsType.DropDown,
                                        label: "Theme",
                                        tooltip: "Defines the colors of button according to theme (available themes: Default, Primary, Secondary, Custom).",
                                        dataPath: "ThemeType",
                                        items: [
                                            { value: ButtonThemeType.Default, label: "Default" },
                                            { value: ButtonThemeType.Primary, label: "Primary" },
                                            { value: ButtonThemeType.Secondary, label: "Secondary" },
                                            { value: ButtonThemeType.Custom, label: "Custom" },
                                        ]
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: "Color",
                                        tooltip: "Used only when button's theme is Custom and specifies its color.",
                                        dataPath: "Color",
                                        isHiddenCell: (d, r, i) => r.ThemeType != ButtonThemeType.Custom
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: "Font Color",
                                        tooltip: "Used only when button's theme is Custom and specifies its font color.",
                                        dataPath: "FontColor",
                                        isHiddenCell: (d, r, i) => r.ThemeType != ButtonThemeType.Custom
                                    },
                                    {
                                        type: SettingsType.Boolean,
                                        label: "Flat",
                                        tooltip: "Reduces the amount of layering on the screen, making it more readable.",
                                        dataPath: "Flat"
                                    },
                                    {
                                        type: SettingsType.Boolean,
                                        label: "Uppercase",
                                        tooltip: "Makes all of the letters uppercase",
                                        dataPath: "Uppercase"
                                    },
                                    {
                                        type: SettingsType.Boolean,
                                        label: 'Has Bubble',
                                        dataPath: 'ShowBubble',
                                        tooltip: 'If enabled, show the bubble indicator'

                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Bubble Text Color',
                                        tooltip: 'Color for the bubble text',
                                        dataPath: 'BubbleColor',
                                        isHiddenCell: (d, r, i) => !r.ShowBubble
                                    },
                                    {
                                        type: SettingsType.ColorPicker,
                                        label: 'Bubble Bg Color',
                                        tooltip: 'Color of bubble background',
                                        dataPath: 'BubbleBackgroundColor',
                                        isHiddenCell: (d, r, i) => !r.ShowBubble
                                    },
                                    {
                                        type: SettingsType.String,
                                        label: "Bubble Content",
                                        tooltip: 'Set Bubble Content.',
                                        dataPath: "BubbleContent",
                                        isHiddenCell: (d, r, i) => !r.ShowBubble
                                    },
                                    {
                                        type: SettingsType.Slider,
                                        label: 'Bubble Size',
                                        tooltip: 'Configure bubble size.',
                                        dataPath: 'BubbleSize',
                                        minValue: 20,
                                        maxValue: 40,
                                        stepValue: 1,
                                        valuePosfix: 'px',
                                        isHiddenCell: (d, r, i) => !r.ShowBubble
                                    }
                                ],
                                createNewItem: (data) => {
                                    // Create button's label and key.
                                    const label: string = 'New button';
                                    const buttonKey: string = WebNode
                                        .GetInstanceKey(props.NodeKey)
                                        .GetThing(props.ThingKey)
                                        .GetNextGeneratedPortKey();

                                    return {
                                        Key: buttonKey,
                                        Label: label,
                                        Value: "",
                                        LabelPosition: ButtonLabelPosition.bottom,
                                        Type: ButtonValueType.Text,
                                        ThemeType: ButtonThemeType.Default,
                                        Flat: false,
                                        Uppercase: true,
                                        Color: 'rgb(0, 188, 212)',
                                    } as ButtonsDataSettings;

                                },
                                onSaveUpdate: (data) => {
                                    let newData = _.cloneDeep(data) as ButtonsWidgetSettingsState;
                                    // Find last y layout's position.
                                    const getLastYLayoutPos = (layout) => {
                                        let y: number = 0;
                                        _.forOwn(layout, (layout) => {
                                            if (layout.Y + layout.H > y)
                                                y = layout.Y + layout.H;
                                        });
                                        return y;
                                    };

                                    // Find any missing buttons from layout
                                    _.forEach(newData.WidgetData.Buttons, (b) => {
                                        if (!yHas(newData.WidgetData.CanvasLayoutDesc, b.Key)) {
                                            newData.WidgetData.CanvasLayoutDesc[b.Key] = {
                                                Key: b.Key,
                                                X: 0,
                                                Y: getLastYLayoutPos(newData.WidgetData.CanvasLayoutDesc),
                                                W: 6,
                                                H: 1,
                                                MinH: 1,
                                                MinW: 1,
                                                IsDraggable: true,
                                                IsResizable: true
                                            }
                                        }
                                        if (!IsNullOrWhiteSpace(b.Label)
                                            && b.Type != ButtonValueType.Text
                                            && (b.LabelPosition == ButtonLabelPosition.bottom || b.LabelPosition == ButtonLabelPosition.top)
                                            && newData.WidgetData.CanvasLayoutDesc[b.Key].H == 1) {
                                            newData.WidgetData.CanvasLayoutDesc[b.Key].H = 1 + newData.WidgetData.CanvasLayoutDesc[b.Key].H;
                                            if (Canvas.LayoutCollisionCheck(newData.WidgetData.CanvasLayoutDesc[b.Key], _.values(newData.WidgetData.CanvasLayoutDesc))) {
                                                newData.WidgetData.CanvasLayoutDesc = Canvas.LayoutAutoReorder(_.values(newData.WidgetData.CanvasLayoutDesc), newData.WidgetData.CanvasLayoutDesc[b.Key]);
                                            }
                                        }
                                    });

                                    // Find any deleted buttons
                                    let deleteKeys = [];
                                    _.forOwn(newData.WidgetData.CanvasLayoutDesc, (c, k) => {
                                        if (_.findIndex(newData.WidgetData.Buttons, (b) => b.Key == k) == -1) {
                                            deleteKeys.push(k);
                                        }
                                    });
                                    newData.WidgetData.CanvasLayoutDesc =
                                        _.omit(newData.WidgetData.CanvasLayoutDesc, deleteKeys);

                                    newData.canvasItems = _.cloneDeep(ButtonsWidgetSettings.setCanvasItems(newData.WidgetData))
                                    return newData;
                                }
                            },
                            {
                                type: SettingsType.Dialog,
                                label: 'Layout',
                                tooltip: 'Configures the buttons layout.',
                                dataPath: '',
                                icon: <i className="fas fa-pen" style={{ fontSize: 16 }} />,
                                dialogConf: {
                                    title: "Layout Buttons",
                                    contentStaticSize: 0.75,
                                    initPrivData: (data) => {
                                        return {
                                            layout: _.cloneDeep(data.WidgetData.CanvasLayoutDesc),
                                            canvasItems: _.cloneDeep(data.canvasItems)
                                        } as ILayoutEditor;
                                    },
                                    actionsFunc: (di) => [
                                        <Button
                                            key='reset'
                                            label='Reset'
                                            borderRadius={0}
                                            shadowLevel={0}
                                            style={{ background: 'transparent', color: red200 }}
                                            onClick={() => {
                                                let layout: CanvasItemLayoutDescDictionary = {};
                                                let canvasItems: CanvasItemDescDictionary = {};

                                                // Set layout for elements.
                                                _.forEach(di.data.WidgetData.Buttons, (v, i) => {
                                                    layout[v.Key] = {
                                                        Key: v.Key,
                                                        X: 0,
                                                        Y: i * 1,
                                                        W: 6,
                                                        H: 1,
                                                        MinH: 1,
                                                        MinW: 1,
                                                        IsDraggable: true,
                                                        IsResizable: true
                                                    };

                                                    // Define item's title.
                                                    let title = '';
                                                    switch (v.Type) {
                                                        case ButtonValueType.Text:
                                                            title = v.Label;
                                                            break;
                                                        case ButtonValueType.FontIcon:
                                                            title = '<i class="fa ' + v.Value + '"></i>';
                                                            break;
                                                        case ButtonValueType.Image:
                                                            title = '<img src="' + v.Value + '" style="height: 33px; width: auto;"/>';
                                                            break;
                                                        default:
                                                            title = v.Label;
                                                            break;
                                                    }

                                                    canvasItems[v.Key] = {
                                                        Key: v.Key,
                                                        Title: title,
                                                        Render: () => { return null; }
                                                    }
                                                });

                                                // Add descriptor for the new item.
                                                di.privData.layout = layout;
                                                di.privData.canvasItems = canvasItems;
                                                di.updatePrivData(di.privData);
                                            }}
                                        />,
                                        <Button
                                            key='cancel'
                                            label='Cancel'
                                            borderRadius={0}
                                            shadowLevel={0}
                                            style={{ background: 'transparent', color: red200 }}
                                            onClick={di.closeDialogCB}
                                        />,
                                        <Button
                                            key='save'
                                            label='Save'
                                            borderRadius={0}
                                            shadowLevel={0}
                                            style={{ background: 'transparent' }}
                                            onClick={() => {
                                                // Update dialog's data and close it.
                                                di.data.WidgetData.CanvasLayoutDesc = _.cloneDeep(di.privData.layout);
                                                di.data.canvasItems = _.cloneDeep(di.privData.canvasItems);
                                                di.updateData(di.data);
                                                di.closeDialogCB();
                                            }}
                                        />
                                    ],
                                    bodyFunc: (w, h, di) =>
                                        <Canvas
                                            GridLayout
                                            CompactLayout
                                            InEditMode
                                            key={getRandomKey()}
                                            width={w}
                                            height={h}
                                            SizeW={6}
                                            Margin={{ W: 6, H: 6 }}
                                            WidthListThreshold={0}
                                            CanvasBackgroundColor='rgb(255, 255, 255)'
                                            Layout={{
                                                LayoutDesc: di.privData.layout,
                                                ItemDesc: di.privData.canvasItems
                                            }}
                                            ElementSettings={{
                                                zDepth: 0,
                                                NoBackground: false,
                                                BackgroundColor: "transparent",
                                                BorderRadius: di.data.WidgetData.BorderRadius
                                            }}
                                            onCanvasLayoutChanged={newLayout =>
                                                // Update layout.
                                                di.updatePrivData({
                                                    ...di.privData,
                                                    layout: newLayout.LayoutDesc
                                                })} />
                                } as SettingsDialogConf<ButtonsWidgetSettingsState, ILayoutEditor>
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Fill Space',
                                tooltip: "Fills the widget's available space.",
                                dataPath: 'WidgetData.FillWidget'
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Max Font Size',
                                dataPath: 'WidgetData.MaxFontSize',
                                tooltip: 'Sets the maximum font size.',
                                minValue: 12,
                                maxValue: 40,
                                stepValue: 1,
                                valuePosfix: 'px'
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Border Radius',
                                dataPath: 'WidgetData.BorderRadius',
                                tooltip: 'Sets the border radius of buttons',
                                minValue: 0,
                                maxValue: 20,
                                stepValue: 1,
                                valuePosfix: 'px'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'On Hover Font Color',
                                dataPath: 'WidgetData.OnHoverFontColor',
                                tooltip: 'When enabled, it sets the text-color of buttons when you mouse over them',
                                isNullable: true
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'On Hover Background Color',
                                dataPath: 'WidgetData.OnHoverBackgroundColor',
                                tooltip: 'When enabled, it sets the background color of buttons when you mouse over them',
                                isNullable: true
                            }
                        ]
                    }
                ]}
                onSaveData={(data) => this.setState(data, () => this.saveData())}
                Toolbar={this.props.SettingListToolbar}
            />
    }
    // ---------------------------------------------------------------------------------------------------
}
// =======================================================================================================
export class ButtonsWidget extends IWidgetThingsClass<ButtonsData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): ReactNode {
        return <Buttons />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <ButtonsWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = (this.WidgetData as ButtonsData);
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        this.getPorts(wData).forEach((port) => {
            thing.AddPort(new WebThingPort(
                port,
                WebThingPortType.Trigger), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        this.getPorts(wData).forEach((port) => {
            ports.push(new WebThingPort(
                port,
                WebThingPortType.Trigger
            ));
        })

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    private getPorts(widgetData: ButtonsData): Plegma.Port[] {
        return widgetData.Buttons.map(btn => {
            return {
                Name: btn.Label,
                PortKey: btn.Key,
                Type: Plegma.ePortType.Boolean,
                ioDirection: Plegma.ioPortDirection.Output,
                State: "False"
            }
        });
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): ButtonsData {
        return {
            Buttons: [],
            IsSingleton: false,
            FillWidget: false,
            CanvasLayoutDesc: null,
            MaxFontSize: 14,
            BorderRadius: 2,
            OnHoverFontColor: '',
            OnHoverBackgroundColor: ''
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return ButtonsWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: ButtonsData = ButtonsWidget.GetDefaultWidgetDataStatic();
        let buttonKey1: string = getRandomSmallKey();
        let buttonKey2: string = getRandomSmallKey();
        let buttonKey3: string = getRandomSmallKey();

        // Fill samples buttons
        data.Buttons = [
            {
                Key: "1_" + buttonKey1,
                Value: "",
                Label: "Button 1",
                LabelPosition: ButtonLabelPosition.bottom,
                LabelAlign: "center",
                Tooltip: null,
                Type: ButtonValueType.Text,
                ThemeType: ButtonThemeType.Default,
                Flat: false,
                Uppercase: true,
                Color: 'rgb(0, 188, 212)',
                FontColor: 'white',
                ShowBubble: false,
                BubbleBackgroundColor: '#D0021B',
                BubbleColor: 'white',
                BubbleSize: 20,
                BubbleContent: '1'

            },
            {
                Key: "2_" + buttonKey2,
                Value: "",
                Label: "Button 2",
                LabelPosition: ButtonLabelPosition.bottom,
                LabelAlign: "center",
                Tooltip: null,
                Type: ButtonValueType.Text,
                ThemeType: ButtonThemeType.Default,
                Flat: false,
                Uppercase: true,
                Color: 'rgb(0, 188, 212)',
                FontColor: 'white',
                ShowBubble: false,
                BubbleBackgroundColor: '#D0021B',
                BubbleColor: 'white',
                BubbleSize: 20,
                BubbleContent: '1'

            },
            {
                Key: "3_" + buttonKey3,
                Value: "",
                Label: "Button 3",
                LabelPosition: ButtonLabelPosition.bottom,
                LabelAlign: "center",
                Tooltip: null,
                Type: ButtonValueType.Text,
                ThemeType: ButtonThemeType.Default,
                Flat: false,
                Uppercase: true,
                Color: 'rgb(0, 188, 212)',
                FontColor: 'white',
                ShowBubble: false,
                BubbleBackgroundColor: '#D0021B',
                BubbleColor: 'white',
                BubbleSize: 20,
                BubbleContent: '1'

            }];

        data.CanvasLayoutDesc = {
            ["1_" + buttonKey1]: {
                Key: "1_" + buttonKey1,
                X: 0,
                Y: 0,
                W: 6,
                H: 1,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            },
            ["2_" + buttonKey2]: {
                Key: "2_" + buttonKey2,
                X: 0,
                Y: 1,
                W: 6,
                H: 1,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            },
            ["3_" + buttonKey3]: {
                Key: "3_" + buttonKey3,
                X: 0,
                Y: 2,
                W: 6,
                H: 1,
                MinH: 1,
                MinW: 1,
                IsDraggable: true,
                IsResizable: true
            }
        };

        return {
            Title: "Buttons",
            WidgetTitle: "",
            Key: "ButtonsWidget",
            Type: "ButtonsWidget",
            Data: data,
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/button.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = ButtonsWidget.GetLocalizationDictionaryStatic(this.WidgetData);
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetLocalizationDictionaryStatic(widgetData: ButtonsData): LocalizationMap {
        let res = {};

        _.forEach(widgetData.Buttons, (b) => {
            // add label (if any)
            if (isDefined(b.Label) && !IsNullOrWhiteSpace(b.Label))
                res[b.Key] = b.Label;

            // add tooltip (if any)
            if (isDefined(b.Tooltip) && !IsNullOrWhiteSpace(b.Tooltip))
                res[getKeyForTooltip(b.Key)] = b.Tooltip;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(widgetData: ButtonsData): ButtonsData {
        if (!isDefined(widgetData.CanvasLayoutDesc)) {
            widgetData.CanvasLayoutDesc = {};

            // Set layout for elements.
            _.forEach(widgetData.Buttons, (element, index) => {
                widgetData.CanvasLayoutDesc[element.Key] = {
                    Key: element.Key,
                    X: 0,
                    Y: index * 1,
                    W: 6,
                    H: 1,
                    MinH: 1,
                    MinW: 1,
                    IsDraggable: true,
                    IsResizable: true
                };

                element.Type = ButtonValueType.Text;
            });
        }

        _.forEach(widgetData.Buttons, v => {
            if (!isDefined(v.ThemeType))
                v.ThemeType = ButtonThemeType.Default;

            if (!isDefined(v.Flat))
                v.Flat = false;

            if (!isDefined(v.Uppercase))
                v.Uppercase = true;

            if (!isDefined(v.Color))
                v.Color = 'rgb(0, 188, 212)';

            if (!isDefined(v.FontColor))
                if (v.Flat)
                    v.FontColor = v.Color;
                else
                    v.FontColor = 'white';

            if (!isDefined(v.LabelPosition))
                v.LabelPosition = ButtonLabelPosition.bottom;

            if (!isDefined(v.Label) && v.Type == ButtonValueType.Text)
                v.Label = v.Value;

            if (!isDefined(v.Value))
                v.Value = "";

            if (!isDefined(v.ShowBubble))
                v.ShowBubble = false;

            if (!isDefined(v.BubbleBackgroundColor))
                v.BubbleBackgroundColor = "#D0021B";

            if (!isDefined(v.BubbleColor))
                v.BubbleBackgroundColor = "white";

            if (!isDefined(v.LabelAlign))
                v.LabelAlign = "center";

        });

        return widgetData;
    }
    // ------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
function getKeyForTooltip(buttonKey: string) {
    return buttonKey + "_tooltip";
}
// =======================================================================================================